import Stack from "@mui/material/Stack"
import {
  FieldSection,
  FormDateField,
  FormEmailField,
  FormRoleConfContext,
  FormSearchField,
  FormSwitchField,
  FormTextField,
} from "@pharmupp/p3-front-commons"
import { useFormContext } from "react-hook-form"
import { useUserRoleConf } from "../../../useUserRoleConf"
import type { UserFormReferential } from "../../useUserFormApi"
import {
  ConditionalBirthDateField,
  ConditionalExternSpecification,
  ConditionalFunctionField,
  ConditionalPhoneField,
  ConditionalRppsField,
  ConditionalTeamFunctionField,
  ConditionalTitleField,
  useConditionalRppsField,
} from "./ConditionalFields"
import { fields } from "./GeneralInfos.fields"
import { formRoleConf, readOnlyConf } from "./GeneralInfos.formRoleConf"

interface GeneralInfosProps {
  referential: UserFormReferential
}

export function GeneralInfos({ referential }: GeneralInfosProps) {
  const { watch } = useFormContext()
  const programmedUnactivate = watch(fields.programmedUnactivate)
  const isUpdate = watch("isUpdate")
  const isReadOnly = watch("isReadOnly")
  const isUpdateUserInformation = watch("isUpdateUserInformation")
  const roleConf = useUserRoleConf({
    roleConf: formRoleConf,
    isUpdate,
    readOnlyConf,
    isReadOnly,
    isUpdateUserInformation,
  })

  return (
    <FormRoleConfContext.Provider value={roleConf}>
      <FieldSection
        title="Identification"
        subtitle="Définir l’email d’accès au compte et le numéro de portable pour l’authentification forte"
      >
        <Stack gap={2}>
          <FormEmailField name={fields.email} label="E-mail" required />
          <ConditionalPhoneField />
        </Stack>
      </FieldSection>

      <FieldSection title="Coordonnées" subtitle="Saisir les coordonnées du membre">
        <Stack gap={2}>
          <ConditionalTitleField />

          <Stack direction="row" gap={2}>
            <FormTextField
              name={fields.lastName}
              inputProps={{ style: { textTransform: "uppercase" } }}
              label="Nom de l'utilisateur"
              fullWidth
              required
            />
            <FormTextField
              name={fields.firstName}
              label="Prénom de l'utilisateur"
              fullWidth
              required
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <FormTextField
              name={fields.birthName}
              inputProps={{ style: { textTransform: "uppercase" } }}
              label="Nom de naissance"
              fullWidth
            />

            <ConditionalBirthDateField />
          </Stack>
        </Stack>
      </FieldSection>

      <FieldSection
        title="Type"
        subtitle="Sélectionner un type de profil utilisateur"
      >
        <FormSearchField
          label="Type"
          name={fields.type}
          options={referential.type}
          required
          valueFormat="optionValue"
        />
        <ConditionalExternSpecification />
        <ConditionalTeamFunctionField />
        <ConditionalFunctionField />
      </FieldSection>

      <RppsSection />

      <FieldSection
        title="Désactivation programmée"
        subtitle="Programmer une date de désactivation ?"
        name="programmedDeactivationSection"
      >
        <FormSwitchField
          name={fields.programmedUnactivate}
          toggleLabels={["Non", "Oui"]}
          data-testid="Désactivation programmée"
        />

        {!!programmedUnactivate && (
          <FormDateField
            label="Date de désactivation"
            name={fields.unactivateDate}
          />
        )}
      </FieldSection>
    </FormRoleConfContext.Provider>
  )
}

const RppsSection = () => {
  const { required, hidden } = useConditionalRppsField()

  if (hidden) {
    return null
  }

  return (
    <FieldSection
      title="Identifiant RPPS"
      subtitle="Renseignez l'identifiant unique de professionnel de santé"
      required={required}
    >
      <ConditionalRppsField />
    </FieldSection>
  )
}
